'use client';

import { Flex, Box, TagProps, Text, Tooltip } from '@chakra-ui/react';
import { FC, useCallback, useMemo, useState } from 'react';
import colors from '@/constants/theme/colors';
import { IProductItem, IProductTag } from '@/types/product';
import { PATH } from '@/constants/paths';
import {
  appActions,
  useCurrencyCodeSelector,
  useCurrencyExchangeRateSelector,
  useWishlistVariantIdsSelector,
} from '@/store/slices/app';
import Link from 'next/link';
import Image from 'next/image';
import {
  displayProductVariantMetal,
  formatPrice,
  getPrimaryMetal,
  getSecondaryMetal,
} from '@/utils/product';
import HeartSvgIcon from '@/components/Icons/Svg/HeartSvgIcon';
import useMobile from '@/hooks/useMobile';
import { find, uniq } from 'lodash';
import { useDispatch } from 'react-redux';
import { useGetLoggedCustomer } from '@/store/apis/app';
import { useUpdateCustomerPersonalProfileMutation } from '@/store/apis/app.customers';
import useToast from '@/hooks/useToast';

export interface ProductItemProps {
  productItem: IProductItem;
  parentPath?: string;
}
const ProductItem: FC<ProductItemProps> = ({
  productItem,
  parentPath = PATH.shopNewArrivals,
}) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { isMobile } = useMobile();
  const wishlistVariantIds = useWishlistVariantIdsSelector() ?? [];
  const selectedCurrencyCode = useCurrencyCodeSelector();
  const currencyExchangeRate = useCurrencyExchangeRateSelector();

  const { loggedCustomer } = useGetLoggedCustomer();
  const [updateCustomerPersonalInfo] =
    useUpdateCustomerPersonalProfileMutation();

  const { productVariant, thumbnailUrl, priceCurrency, product } = productItem;

  const [imgUrl, setImgUrl] = useState(thumbnailUrl);

  const {
    metal,
    id: productVariantId,
    sku,
    price,
    hoveringPhotoUrls,
    title: variantTitle,
  } = productVariant;
  const { title } = product;

  const wishListProductVariantIds = useMemo(
    () => loggedCustomer?.wishListProductVariantIds ?? [],
    [loggedCustomer]
  );

  const isAddedToWishlist = !!find(
    uniq([...wishlistVariantIds, ...wishListProductVariantIds]),
    (id) => id === productVariantId
  );

  const currency = selectedCurrencyCode ?? priceCurrency;
  const displayPrice = `${formatPrice(currency)(
    price * currencyExchangeRate,
    undefined,
    undefined,
    2
  )}`;

  const displayName = useMemo(() => {
    return variantTitle ?? title;
  }, [title, variantTitle]);

  const primaryMetal = getPrimaryMetal(metal);
  const secondaryMetal = getSecondaryMetal(metal);

  const handleAddVariantToWishlist = useCallback(
    async (variantId: number) => {
      dispatch(appActions.addVariantToWishlist(variantId));
      if (loggedCustomer) {
        const { wishListProductVariantIds = [] } = loggedCustomer;

        await updateCustomerPersonalInfo({
          params: {
            customerId: loggedCustomer.id,
          },
          body: {
            wishListProductVariantIds: uniq([
              ...wishListProductVariantIds,
              variantId,
            ]),
          },
        });
      }

      toast('Added to wishlist', 'success');
    },
    [loggedCustomer, dispatch, toast, updateCustomerPersonalInfo]
  );

  const handleRemoveVariantFromWishlist = useCallback(
    async (variantId: number) => {
      dispatch(appActions.removeVariantFromWishlist(variantId));

      if (loggedCustomer) {
        const { wishListProductVariantIds = [] } = loggedCustomer;

        await updateCustomerPersonalInfo({
          params: {
            customerId: loggedCustomer.id,
          },
          body: {
            wishListProductVariantIds: wishListProductVariantIds.filter(
              (id) => id !== variantId
            ),
          },
        });
      }

      toast('Removed from wishlist', 'success');
    },
    [loggedCustomer, updateCustomerPersonalInfo, toast, dispatch]
  );

  const handleAddToWishList = () => {
    const { productVariant } = productItem;
    handleAddVariantToWishlist(productVariant.id);
  };

  const handleRemoveFromWishList = () => {
    const { productVariant } = productItem;
    handleRemoveVariantFromWishlist(productVariant.id);
  };

  return (
    <Flex
      w="100%"
      flexDir="column"
      bgColor={colors.white}
      onMouseOver={() => {
        const secondImgUrl = hoveringPhotoUrls?.[0];
        if (secondImgUrl) setImgUrl(secondImgUrl);
      }}
      onMouseLeave={() => {
        setImgUrl(thumbnailUrl);
      }}
      onClick={() => {
        if (!isMobile) return;
        const firstImgUrl = thumbnailUrl;
        const secondImgUrl = hoveringPhotoUrls?.[0];
        if (!secondImgUrl || !firstImgUrl) return;
        setImgUrl((prev) =>
          prev === firstImgUrl ? secondImgUrl : firstImgUrl
        );
      }}
    >
      <Link href={`${parentPath}/${productItem.slug}?sku=${sku}`}>
        <Flex
          flexDir="column"
          position="relative"
          w="100%"
          h={{ base: '12.75rem', xl: '22.5rem' }}
          justifyContent="flex-end"
          p="0.5rem"
        >
          <Box
            position="absolute"
            zIndex="10"
            right="0.5rem"
            top="0.5rem"
            p="0.5rem"
            _hover={{
              transform: 'scale(1.25)',
            }}
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              if (isAddedToWishlist) {
                handleRemoveFromWishList();
                return;
              }

              handleAddToWishList();
            }}
          >
            <Tooltip
              hasArrow
              placement="bottom-end"
              label={
                isAddedToWishlist ? 'Remove from wishlist' : 'Add to wishlist'
              }
            >
              <Box>
                <HeartSvgIcon
                  fill={isAddedToWishlist ? colors.primary[25] : colors.white}
                  width="1.5rem"
                />
              </Box>
            </Tooltip>
          </Box>
          {imgUrl ? (
            <Image
              alt={displayName}
              src={imgUrl}
              fill
              sizes="(max-width: 992px) 175px, 270px"
              style={{ objectFit: 'cover' }}
              priority
              loading="eager"
              quality={100}
              unoptimized
            />
          ) : (
            <Flex
              w="100%"
              h="100%"
              alignItems="center"
              justifyContent="center"
              position="absolute"
              bgColor={colors.grey[100]}
            >
              <Text color={colors.primary[300]} textTransform="uppercase">
                no image
              </Text>
            </Flex>
          )}
          <Flex flexDir="column" zIndex="10" gap="0.5rem">
            {/* TODO: handle tags */}
          </Flex>
        </Flex>
        <Flex
          flexDir="column"
          p={{ base: '0.75rem', xl: '0.75rem 1rem 1rem' }}
          gap="0.25rem"
        >
          <Flex gap="0.5rem" alignItems="center">
            <Text
              color={displayProductVariantMetal(primaryMetal).color}
              fontSize={{ base: '0.75rem', xl: '0.875rem' }}
              fontWeight="300"
              lineHeight={{ base: '1rem', xl: '1.25rem' }}
            >
              {displayProductVariantMetal(primaryMetal).value}
            </Text>
            {!!secondaryMetal && (
              <Text
                color={displayProductVariantMetal(secondaryMetal).color}
                fontSize={{ base: '0.75rem', xl: '0.875rem' }}
                fontWeight="300"
                lineHeight={{ base: '1rem', xl: '1.25rem' }}
              >
                {displayProductVariantMetal(secondaryMetal).value}
              </Text>
            )}
          </Flex>

          <Text
            color={colors.primary[300]}
            fontSize={{ base: '0.875rem', xl: '1rem' }}
            fontWeight="400"
            lineHeight={{ base: '1.25rem', xl: '1.5rem' }}
            letterSpacing={{ base: '-0.06px', xl: 'normal' }}
          >
            {displayName}
          </Text>
          <Text
            color={colors.primary[100]}
            fontSize={{ base: '0.875rem', xl: '1rem' }}
            fontWeight="400"
            lineHeight={{ base: '1.25rem', xl: '1.5rem' }}
          >
            {displayPrice}
          </Text>
        </Flex>
      </Link>
    </Flex>
  );
};

interface ProductTagProps extends TagProps {
  tag: IProductTag;
}

export default ProductItem;
